import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ClientPortalUserDetailsModel, ClientPortalUserModel } from "services/api/cplauthapi/CPLAuthAPI";
import { container } from "setup/dependency/inversify.config";
import { IClientPortalUsersService } from "services/ClientPortalUsersService";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { toast } from "react-toastify";
import { RootState } from "setup/redux/RootReducer";

type UserProfileProps = {
  UserProfile: ClientPortalUserModel;
  IsBusy: boolean;
  ShowEmailForm: boolean;
  NewEmail: string;
  ShowPasswordForm: boolean;
  ShowVerificationCodeForm: boolean;
};

const initialState: UserProfileProps = {
  UserProfile: new ClientPortalUserModel({ firstName: "", lastName: "", emailAddress: "" }),
  IsBusy: false,
  ShowEmailForm: false,
  NewEmail: "",
  ShowPasswordForm: false,
  ShowVerificationCodeForm: false,
};

export const getClientPortalUserProfile = createAsyncThunk("UserProfile/GetProfile", async () => {
  const usersService = container.get<IClientPortalUsersService>(SERVICE_TYPES.ClientPortalUsersService);
  return await usersService.getProfile();
});

export const changeClientPortalUserDetails = createAsyncThunk(
  "UserProfile/ChangeClientPortalUserDetails",
  async (clientPortalUserDetailsModel: ClientPortalUserDetailsModel, thunkAPI) => {
    const usersService = container.get<IClientPortalUsersService>(SERVICE_TYPES.ClientPortalUsersService);
    await usersService.changeClientPortalUserDetails(clientPortalUserDetailsModel);
    thunkAPI.dispatch(getClientPortalUserProfile());
  }
);

export const changeClientPortalUserPassword = createAsyncThunk(
  "UserProfile/ChangeClientPortalUserPassword",
  async (newPassword: string, thunkAPI) => {
    const usersService = container.get<IClientPortalUsersService>(SERVICE_TYPES.ClientPortalUsersService);
    await usersService.changeClientPortalUserPassword(newPassword);
    thunkAPI.dispatch(getClientPortalUserProfile());
  }
);

export const changeClientPortalUserEmail = createAsyncThunk<void, { verificationCode: string }, { state: RootState }>(
  "UserProfile/ChangeClientPortalUserEmail",
  async ({ verificationCode }, thunkAPI) => {
    const clientPortalUsersService = container.get<IClientPortalUsersService>(SERVICE_TYPES.ClientPortalUsersService);
    const state = thunkAPI.getState();
    await clientPortalUsersService.changeClientPortalUserEmail(
      state.userProfile.NewEmail,
      verificationCode,
      state.companyProvider.companyId ?? ""
    );
    thunkAPI.dispatch(getClientPortalUserProfile());
  }
);

export const sendClientPortalUserEmailVerificationCode = createAsyncThunk<
  string,
  { emailAddress: string },
  { state: RootState }
>("UserProfile/VerifyClientPortalUserEmail", async ({ emailAddress }, thunkAPI) => {
  const clientPortalUsersService = container.get<IClientPortalUsersService>(SERVICE_TYPES.ClientPortalUsersService);
  const state = thunkAPI.getState();
  await clientPortalUsersService.sendEmailVerificationCode(emailAddress, state.companyProvider.companyId ?? "");
  return emailAddress;
});

export const userProfileSlice = createSlice({
  name: "UserProfile",
  initialState,
  reducers: {
    setShowPasswordForm: (state, action) => {
      state.ShowPasswordForm = action.payload;
    },
    setShowEmailForm: (state, action) => {
      state.ShowEmailForm = action.payload;
    },
    setShowVerificationCodeForm: (state, action) => {
      state.ShowVerificationCodeForm = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getClientPortalUserProfile.fulfilled, (state, action) => {
        state.UserProfile = action.payload;
      })
      .addCase(changeClientPortalUserDetails.pending, (state) => {
        state.IsBusy = true;
      })
      .addCase(changeClientPortalUserDetails.rejected, (state) => {
        toast.error("Cannot Update Profile Details");
        state.IsBusy = false;
      })
      .addCase(changeClientPortalUserDetails.fulfilled, (state) => {
        toast.success("Profile Details Updated");
        state.IsBusy = false;
      })
      .addCase(changeClientPortalUserPassword.pending, (state) => {
        state.IsBusy = true;
      })
      .addCase(changeClientPortalUserPassword.rejected, (state) => {
        toast.error("Cannot Update Profile Password");
        state.IsBusy = false;
        state.ShowPasswordForm = false;
      })
      .addCase(changeClientPortalUserPassword.fulfilled, (state) => {
        toast.success("Profile Password Updated");
        state.IsBusy = false;
        state.ShowPasswordForm = false;
      })
      .addCase(changeClientPortalUserEmail.pending, (state) => {
        state.IsBusy = true;
      })
      .addCase(changeClientPortalUserEmail.rejected, (state) => {
        toast.error("Cannot Update Profile Email");
        state.IsBusy = false;
        state.ShowEmailForm = false;
        state.ShowVerificationCodeForm = false;
      })
      .addCase(changeClientPortalUserEmail.fulfilled, (state) => {
        toast.success("Profile Email Updated");
        state.IsBusy = false;
        state.ShowEmailForm = false;
        state.ShowVerificationCodeForm = false;
      })
      .addCase(sendClientPortalUserEmailVerificationCode.fulfilled, (state, action) => {
        toast.success("Email Verification Code Sent");
        state.NewEmail = action.payload;
        state.IsBusy = false;
        state.ShowVerificationCodeForm = true;
      })
      .addCase(sendClientPortalUserEmailVerificationCode.pending, (state) => {
        state.IsBusy = true;
      })
      .addCase(sendClientPortalUserEmailVerificationCode.rejected, (state) => {
        toast.error("Cannot Send Email Verification Code");
        state.IsBusy = false;
        state.ShowEmailForm = false;
        state.ShowVerificationCodeForm = false;
        state.NewEmail = "";
      });
  },
});

export const { setShowEmailForm, setShowPasswordForm, setShowVerificationCodeForm } = userProfileSlice.actions;

export const selectUserProfile = (state: RootState) => {
  return state.userProfile.UserProfile;
};
export const selectShowEmailForm = (state: RootState) => {
  return state.userProfile.ShowEmailForm;
};
export const selectNewEmail = (state: RootState) => {
  return state.userProfile.NewEmail;
};
export const selectShowPasswordForm = (state: RootState) => {
  return state.userProfile.ShowPasswordForm;
};
export const selectShowVerificationCodeForm = (state: RootState) => {
  return state.userProfile.ShowVerificationCodeForm;
};
export const selectIsBusy = (state: RootState) => {
  return state.userProfile.IsBusy;
};

export default userProfileSlice.reducer;
